import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { InsightsDateRangePicker } from 'components/filter/modules/insights';
import { CountrySelect } from 'components/filter/input/CountrySelect';
import { BrandSelect } from 'components/filter/input/BrandSelect';
import { MarketplaceSelect } from 'components/filter/input/MarketplaceSelect';
import { InfoButton } from 'components/InfoButton';
import { FilterDialogButton } from 'components/filter/advancedFilter';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { Tab, Tabs } from '@mui/material';
import { PageHeader } from 'components/Layout/PageContainer';
interface InsightsTab {
  route: string;
  messageId: string;
  defaultMessage: string;
}

const insightsTabs: InsightsTab[] = [
  {
    route: '/insights/performance',
    messageId: 'insights.performance_tab',
    defaultMessage: 'Performance',
  },
  {
    route: '/insights/marketplace',
    messageId: 'insights.marketplace_tab',
    defaultMessage: 'Marketplace',
  },
  {
    route: '/insights/country',
    messageId: 'insights.country_tab',
    defaultMessage: 'Country',
  },
  {
    route: '/insights/category',
    messageId: 'insights.category_tab',
    defaultMessage: 'Category',
  },
  {
    route: '/insights/product',
    messageId: 'insights.product_tab',
    defaultMessage: 'Product',
  },
];

function InsightsPro() {
  const navigate = useNavigate();
  const { matchRoute } = useRouteMatch(insightsTabs);
  const currentSubPage = (matchRoute() ?? insightsTabs[0]) as InsightsTab;
  const location = useLocation();
  const { hiddenFilters } = useTransitiveCacheStore();

  const [searchParams] = useSearchParams();

  const isDateRangeHidden = hiddenFilters?.includes('dateRange') ?? false;
  const isComparisonDateRangeHidden = hiddenFilters?.includes('comparisonDateRange') ?? false;

  const handleTabNavigation = (targetPath: string | null) => {
    if (targetPath !== null) {
      const cleanPath = targetPath.replace('/*', '');
      const params = searchParams.toString();
      if (location.pathname !== cleanPath) {
        navigate(`${cleanPath}?${params}`);
      } else {
        navigate(`${cleanPath}?${params}`, { replace: true });
      }
    }
  };

  return (
    <div className="insights-pro">
      <PageHeader
        topRightComponent={<InfoButton tooltipText="View dashboard information" showText />}
        tabs={
          <Tabs
            value={currentSubPage.route}
            onChange={(_: unknown, newVal: string | null) => handleTabNavigation(newVal)}
          >
            {insightsTabs.map(tab => (
              <Tab
                key={tab.route}
                value={tab.route}
                data-testid={tab.messageId}
                id={tab.messageId}
                label={<FormattedMessage id={tab.messageId} defaultMessage={tab.defaultMessage} />}
                onClick={() => handleTabNavigation(tab.route)}
                className="hover:bg-denim-50"
              />
            ))}
          </Tabs>
        }
      >
        {!isDateRangeHidden && <InsightsDateRangePicker.Default caption="filter.key.selectedDateRange" />}
        {!isComparisonDateRangeHidden && (
          <InsightsDateRangePicker.Comparison caption="filter.key.selectedDateRangeComparison" />
        )}
        <CountrySelect />
        <BrandSelect />
        <MarketplaceSelect />
        <FilterDialogButton module="insights" />
      </PageHeader>
      <div className="px-10">
        <Outlet />
      </div>
    </div>
  );
}

export { InsightsPro };
